import type { DesktopNativeBrowserResult } from "@cmmd-forge/contracts";
import { useEffect, useRef } from "react";
import { useCommandPaletteStore } from "~/commandPaletteStore";

interface DesktopNativeBrowserSurfaceProps {
  readonly active: boolean;
  readonly browserId: string | null;
  readonly sessionId: string | null;
  readonly tabId: string | null;
  readonly url: string | null;
  readonly onError?: (error: unknown) => void;
  readonly onResult?: (result: DesktopNativeBrowserResult) => void;
}

function measuredBounds(element: HTMLElement): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.x,
    y: rect.y,
    width: rect.width,
    height: rect.height,
  };
}

export function DesktopNativeBrowserSurface(props: DesktopNativeBrowserSurfaceProps) {
  const { active, browserId, onError, onResult, sessionId, tabId, url } = props;
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const attachedRef = useRef(false);
  const commandPaletteOpen = useCommandPaletteStore((state) => state.open);
  const effectiveActive = active && !commandPaletteOpen;

  useEffect(() => {
    const nativeBrowser = window.desktopBridge?.nativeBrowser;
    const anchor = anchorRef.current;
    if (!nativeBrowser || !anchor || !browserId || !sessionId) {
      attachedRef.current = false;
      return;
    }

    let cancelled = false;
    // Guards against issuing a second attach() while the first is still in
    // flight. attachedRef is only flipped true once attach resolves (below),
    // so without this a resize during the pending attach would re-attach.
    let attachInFlight = false;
    const identity = {
      browserId,
      sessionId,
      ...(tabId ? { tabId } : {}),
    };

    if (!effectiveActive) {
      const detachOperation = commandPaletteOpen
        ? (nativeBrowser.detachAll?.() ?? nativeBrowser.detach(identity))
        : nativeBrowser.detach(identity);
      void detachOperation.catch(() => undefined);
      attachedRef.current = false;
      return;
    }

    const syncBounds = () => {
      const bounds = measuredBounds(anchor);
      if (bounds.width <= 0 || bounds.height <= 0) {
        return;
      }
      const input = {
        ...identity,
        bounds,
      };
      const isAttached = attachedRef.current;
      if (!isAttached && attachInFlight) {
        // An attach is already pending; don't fire a duplicate.
        return;
      }
      const operation = isAttached
        ? nativeBrowser.updateBounds(input)
        : nativeBrowser.attach({
            ...input,
            url: url ?? "about:blank",
          });
      if (!isAttached) {
        attachInFlight = true;
      }
      void operation.then(
        (result) => {
          attachInFlight = false;
          if (cancelled) {
            return;
          }
          // result.attached is the authoritative signal — only mark attached
          // once the native side confirms it, never optimistically.
          attachedRef.current = result.attached;
          onResult?.(result);
        },
        (error: unknown) => {
          attachInFlight = false;
          if (cancelled) {
            return;
          }
          attachedRef.current = false;
          onError?.(error);
        },
      );
    };

    syncBounds();
    const observer = new ResizeObserver(syncBounds);
    observer.observe(anchor);
    window.addEventListener("resize", syncBounds);
    window.addEventListener("scroll", syncBounds, true);

    return () => {
      cancelled = true;
      observer.disconnect();
      window.removeEventListener("resize", syncBounds);
      window.removeEventListener("scroll", syncBounds, true);
      if (attachedRef.current) {
        attachedRef.current = false;
        void nativeBrowser.detach(identity).catch(() => undefined);
      }
    };
  }, [effectiveActive, browserId, sessionId, tabId, onError, onResult, url, commandPaletteOpen]);

  return (
    <div ref={anchorRef} aria-hidden="true" className="pointer-events-none absolute inset-0" />
  );
}
